import React, { ReactNode } from 'react';
import { Box, Card, CardContent, Chip, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TrendingDown, TrendingUp } from 'lucide-react';

export interface KPICardProps {
  kpi: {
    label: string;
    value: number | string;
    unit?: string;
    trend?: number | null;
    trendDirection?: 'up' | 'down' | null;
  };
  loading?: boolean;
  icon?: ReactNode;
}

export const KPICard: React.FC<KPICardProps> = ({ kpi, loading = false, icon }) => {
  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="80%" height={20} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={56} height={20} sx={{ borderRadius: 9999, mt: 1.5 }} />
        </CardContent>
      </Card>
    );
  }

  const trendVal = kpi.trend;
  const hasTrend = trendVal !== undefined && trendVal !== null && kpi.trendDirection;
  const isUp = kpi.trendDirection === 'up';

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxSizing: 'border-box',
          '&:last-child': { pb: 2.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
              {kpi.value}
              {kpi.unit && (
                <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                  {kpi.unit}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {kpi.label}
            </Typography>
          </Box>
        </Box>

        {hasTrend && (
          <Box sx={{ mt: 'auto', pt: 1.5 }}>
            <Chip
              size="small"
              icon={isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              label={`${Math.abs(trendVal)}%`}
              color={isUp ? 'success' : 'error'}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 22,
                '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
